import { RootState } from 'store/types';
import { createSelector } from 'reselect';
import { DeepLinkAction } from './types';
import { RouteLinkGenerator } from './utils';

export const deepLinkSelector = (state: RootState) => state.deepLink;

export const deepLinkActionSelector = createSelector(deepLinkSelector, deeplink => deeplink.action);

export const deepLinkFetchingSelector = createSelector(
  deepLinkActionSelector,
  action => action && action.isFetching
);

export const routeLink = (action: DeepLinkAction) => {
  const { type, id, sourceId, relatedData } = action;

  switch (type) {
    case 'medication:update':
      return RouteLinkGenerator.medication(sourceId, relatedData);
    case 'visitSummary:created':
      return RouteLinkGenerator.visitSummary(sourceId, relatedData);
    case 'goal:created':
      return RouteLinkGenerator.goals(sourceId);
    case 'patientEducation:create':
      return RouteLinkGenerator.patientEducation(id, relatedData);
    case 'appointmentReminder:update':
      return RouteLinkGenerator.upcomingAppointment(sourceId);
    case 'videoVisitReminder:created':
      return RouteLinkGenerator.videoVisit(sourceId);
    case 'message:received':
      return RouteLinkGenerator.messaging(sourceId);
    case 'bill:new':
      return RouteLinkGenerator.newBill(sourceId);
    case 'bill:reminder':
    case 'bill:pastDue':
    case 'bill:overdue':
      return RouteLinkGenerator.unpaidBill(sourceId);
    case 'announcement:new':
    case 'antiCoagulation:create':
    case 'healthAdvisory:created':
    case 'rta:created':
      // Surrogate UUID's are the future. If we have one, use it. Otherwise fallback to the id.
      return RouteLinkGenerator.dashboard(sourceId, {
        goToId: relatedData?.surrogateUuid ?? id,
        ...relatedData
      });
    case 'imagingReport:created':
    case 'pathologyReport:created':
    case 'labResult:created':
      return RouteLinkGenerator.testResults(sourceId);
    case 'patientLetter:create':
      return RouteLinkGenerator.document();
    case 'visitPay:*':
      return RouteLinkGenerator.visitPay(sourceId);
    default:
      return RouteLinkGenerator.default(sourceId);
  }
};

export const deepLinkRouteSelector = createSelector([deepLinkActionSelector], action => {
  if (action && action.type) {
    const { consumerId } = action;
    const { route, amwell = false, params } = routeLink(action);
    if (route) {
      return {
        consumerId,
        amwell,
        route,
        params
      };
    }
  }
  return null;
});

export const deepLinkUrlSelector = createSelector(
  deepLinkSelector,
  deeplink => deeplink.deepLinkUrl
);
